<script lang="ts">
  import { DirectusImage, Preset } from '@do/svelte-directus-images';

  import { AvatarPlaceholder } from '@shared/ui/avatar-placeholder';
  import { Container } from '@shared/ui/container';
  import { Content } from '@shared/ui/content';
  import { ImageSkeleton } from '@shared/ui/image-skeleton';
  import { ReadMore } from '@shared/ui/read-more';
  import { cn } from '@shared/utils';

  import type { CuratorType } from '@entities/curator';

  import CuratorSocials from './curator-socials.svelte';

  export let curator: CuratorType;

  const imgClass =
    'xs:w-[360px] xs:h-[360px] md:w-[400px] md:h-[400px] aspect-square lg:w-[416px] lg:h-[416px] object-cover';

  let image: HTMLImageElement;

  $: imageLoaded = image?.complete || !curator.photo;
</script>

<Container>
  <div class="mb-8 mt-8 grid gap-x-0 md:mt-11 md:gap-x-12 lg:mb-12 lg:mt-12 lg:grid-cols-12 lg:gap-y-5">
    {#if curator?.biography}
      <div class="4xl:col-span-4 order-3 mb-9 md:mb-9 lg:col-span-7 lg:col-start-1 lg:row-start-2 lg:mb-4">
        <h2 class="mb-3 text-lg font-medium">Biography</h2>

        <ReadMore collapseHeight="{240}" readMoreText="Show more" readLessText="Show less">
          <Content blocks="{curator.biography.blocks}" />
        </ReadMore>
      </div>
    {/if}

    <div class="4xl:col-span-4 order-1 mb-8 lg:order-1 lg:col-span-7 lg:col-start-1 lg:row-start-1 lg:mb-0">
      <div>
        <h1 class="text-4.5xl text-center font-medium md:text-6xl lg:text-start">
          {curator.firstName + ' ' + curator.lastName}
        </h1>
      </div>

      {#if curator?.position}
        <div class="mt-2 text-center font-medium lg:text-start">
          <p class="text-default-500 text-base">{curator.position}</p>
        </div>
      {/if}
    </div>

    <div class="4xl:col-span-4 order-4 lg:col-span-7 lg:col-start-1">
      <CuratorSocials socialProfiles="{curator.socialProfiles}" />
    </div>

    <div class="4xl:col-span-7 4xl:col-start-6 order-2 mb-8 lg:col-span-6 lg:row-start-1 lg:row-end-4">
      <div class="sticky top-20 mx-auto flex flex-col items-center justify-center gap-3 lg:mb-0">
        <div class="overflow-hidden rounded-lg">
          <DirectusImage
            imageData="{curator?.photo}"
            alt="{curator.firstName + ' ' + curator.lastName}"
            imgClasses="hover:scale-[103%] transition-transform duration-300 {imgClass}"
            loading="lazy"
            pictureSizes="{{
              default: '100vw',
              'min-1024': '800px',
              'min-1280': '840px',
            }}"
            presetKeys="{Preset.Thumbnail}"
            on:load="{() => (imageLoaded = true)}"
            bind:imgElement="{image}"
          >
            <AvatarPlaceholder
              slot="default-placeholder"
              rounded="{false}"
              name="{curator.firstName + ' ' + curator.lastName}}"
              classes="text-6xl {imgClass}"
            />
          </DirectusImage>
        </div>

        <div
          class="{cn('absolute top-0 flex w-full items-center justify-center', imgClass, {
            invisible: imageLoaded,
            visible: !imageLoaded,
          })}"
        >
          <ImageSkeleton src="/images/artist-placeholder.png" wrapClasses="{imgClass}" />
        </div>

        <p class="text-default-500 max-w-[360px] text-center text-xs">{curator.photoAttribution}</p>
      </div>
    </div>
  </div>
</Container>
